/**
 * @fileoverview Dropdown de filas com popover, busca e highlight colorido por fila.
 *
 * Padrão visual reaproveitado do NewTicketModal (trigger arredondado + popover com
 * search, lista rolável e background tingido com a cor da fila).
 */

import React, { useMemo, useState } from "react";
import {
  ButtonBase,
  Popover,
  Typography,
  IconButton,
  Divider,
  InputBase,
  makeStyles,
} from "@material-ui/core";
import { Check, Search, X, ChevronDown } from "lucide-react";
const getColorWithOpacity = (color, opacity, fallback) => {
  if (!color || typeof color !== "string") return fallback;
  const normalized = color.trim();
  const hexMatch = normalized.match(/^#([0-9a-f]{3}){1,2}$/i);
  if (hexMatch) {
    let hex = normalized.slice(1);
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  const rgbaMatch = normalized.match(
    /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)$/i
  );
  if (rgbaMatch) {
    const [, r, g, b] = rgbaMatch;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return fallback;
};

const useStyles = makeStyles((theme) => ({
  trigger: {
    minWidth: 190,
    height: 48,
    borderRadius: 16,
    padding: theme.spacing(0, 1.5),
    justifyContent: "space-between",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(248, 250, 252, 0.9)",
  },
  triggerFullWidth: {
    width: "100%",
  },
  triggerContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.8),
    minWidth: 0,
    flex: 1,
  },
  triggerLabel: {
    display: "block",
    fontSize: "0.98rem",
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  triggerIcon: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  placeholder: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.55)" : "#6b7280",
  },
  popover: {
    "& .MuiPopover-paper": {
      width: 320,
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(100vh - 48px)",
      display: "flex",
      flexDirection: "column",
      marginTop: theme.spacing(1),
      borderRadius: 20,
      overflow: "hidden",
      background: theme.palette.type === "dark" ? "#22262b" : "#ffffff",
      color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
      border:
        theme.palette.type === "dark"
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid rgba(15, 23, 42, 0.08)",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 20px 48px rgba(0, 0, 0, 0.45)"
          : "0 20px 44px rgba(15, 23, 42, 0.16)",
    },
  },
  popoverHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.75, 2, 1),
  },
  popoverTitle: {
    fontSize: "1rem",
    fontWeight: 700,
  },
  popoverClose: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  popoverBody: {
    padding: theme.spacing(0, 1.5, 1.5),
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  searchField: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.7, 1.4),
    borderRadius: 16,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(248, 250, 252, 0.9)",
  },
  searchIcon: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  searchInput: {
    flex: 1,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    "& input::placeholder": {
      color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.55)" : "#6b7280",
      opacity: 1,
    },
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.25, 0.5, 1.25),
  },
  statusText: {
    fontSize: "0.95rem",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.76)" : "#6b7280",
  },
  divider: {
    marginBottom: theme.spacing(0.75),
  },
  optionsScrollArea: {
    maxHeight: 320,
    minHeight: 0,
    overflowY: "auto",
    paddingRight: theme.spacing(0.5),
    marginRight: theme.spacing(-0.5),
  },
  option: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.1, 1.4),
    borderRadius: 14,
    marginBottom: theme.spacing(0.75),
    textAlign: "left",
    background: "transparent",
  },
  optionSelected: {
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.16)"
        : "rgba(59, 130, 246, 0.14)",
  },
  optionLabel: {
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
  },
  optionCheck: {
    marginLeft: theme.spacing(1.5),
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "#7aa2ff" : theme.palette.primary.main,
  },
  emptyState: {
    padding: theme.spacing(1.5, 0.5, 0.5),
    fontSize: "0.94rem",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.6)" : "#6b7280",
  },
}));

/**
 * @param value — id numérico da fila selecionada, ou `null` pra "Sem fila", ou
 *   `undefined` pra nenhum estado ("placeholder").
 * @param onChange — recebe `null` quando o usuário escolhe "Sem fila" ou o id.
 */
const QueuePickerPopover = ({
  value,
  onChange,
  queues = [],
  placeholder,
  title,
  searchPlaceholder,
  noQueueLabel,
  showNoQueueOption = true,
  singleSelectedLabel,
  showingAllLabel,
  noQueuesFoundLabel,
  disabled = false,
  fullWidth = false,
  className,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };

  const handleSelect = (queueId) => {
    onChange(queueId);
    handleClose();
  };

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return queues;
    return queues.filter((queue) => (queue.name || "").toLowerCase().includes(q));
  }, [queues, searchTerm]);

  const selectedQueue =
    value !== undefined && value !== null && value !== ""
      ? queues.find((q) => q.id === value) || null
      : null;

  const isNoQueueSelected = value === null;
  const isPlaceholder = !selectedQueue && !isNoQueueSelected;
  const selectedCount = selectedQueue || isNoQueueSelected ? 1 : 0;

  const triggerLabel = selectedQueue
    ? selectedQueue.name
    : isNoQueueSelected
    ? noQueueLabel
    : placeholder;

  return (
    <>
      <ButtonBase
        className={[
          classes.trigger,
          fullWidth ? classes.triggerFullWidth : "",
          className || "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleOpen}
        disabled={disabled}
      >
        <div className={classes.triggerContent}>
          <Typography
            className={`${classes.triggerLabel} ${
              isPlaceholder ? classes.placeholder : ""
            }`}
          >
            {triggerLabel}
          </Typography>
        </div>
        <ChevronDown className={classes.triggerIcon} />
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className={classes.popover}
      >
        <div className={classes.popoverHeader}>
          <Typography className={classes.popoverTitle}>{title}</Typography>
          <IconButton
            size="small"
            className={classes.popoverClose}
            onClick={handleClose}
          >
            <X size={18} />
          </IconButton>
        </div>

        <div className={classes.popoverBody}>
          <div className={classes.searchField}>
            <Search className={classes.searchIcon} />
            <InputBase
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className={classes.searchInput}
            />
          </div>

          <div className={classes.statusRow}>
            <Typography className={classes.statusText}>
              {selectedCount === 1
                ? `1 ${singleSelectedLabel}`
                : showingAllLabel}
            </Typography>
          </div>

          <Divider className={classes.divider} />

          <div className={classes.optionsScrollArea}>
            {showNoQueueOption && (
              <ButtonBase
                className={`${classes.option} ${
                  isNoQueueSelected ? classes.optionSelected : ""
                }`}
                onClick={() => handleSelect(null)}
              >
                <Typography className={classes.optionLabel}>
                  {noQueueLabel}
                </Typography>
                {isNoQueueSelected && (
                  <Check className={classes.optionCheck} />
                )}
              </ButtonBase>
            )}

            {filtered.length === 0 ? (
              <Typography className={classes.emptyState}>
                {noQueuesFoundLabel}
              </Typography>
            ) : (
              filtered.map((queue) => {
                const isSelected = selectedQueue?.id === queue.id;
                const background = getColorWithOpacity(
                  queue.color,
                  isSelected ? 0.26 : 0.16,
                  isSelected
                    ? "rgba(59, 130, 246, 0.18)"
                    : "rgba(59, 130, 246, 0.08)"
                );
                return (
                  <ButtonBase
                    key={queue.id}
                    className={`${classes.option} ${
                      isSelected ? classes.optionSelected : ""
                    }`}
                    onClick={() => handleSelect(queue.id)}
                    style={{ backgroundColor: background }}
                  >
                    <Typography className={classes.optionLabel}>
                      {queue.name}
                    </Typography>
                    {isSelected && (
                      <Check className={classes.optionCheck} />
                    )}
                  </ButtonBase>
                );
              })
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default QueuePickerPopover;
